import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCheck,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaTimes,
} from "react-icons/fa";
import { ReactComponent as TrashIcon } from "../../../assets/icons/trash.svg";
import useReaderStore from "../../../context/readerStore";
import {
  buildAiContext,
  deleteQuizResult,
  executeAiAction,
  listQuizResults,
  saveQuizResult,
} from "../../../services/aiService";
import { getSessionTokens } from "../../../services/igpService";
import { showInfoNotification } from "../../../services/notificationService";
import AiFeedback from "../AiFeedback";
import QuizModal from "../quiz/QuizModal";
import "../quiz/QuizModal.css";
import { QuizOption } from "../quiz/QuizQuestion";
import { getQuizFeedbackKey } from "../quiz/useQuizSession";

const SCORE_RING_RADIUS = 34;
const SCORE_RING_CIRCUMFERENCE = 2 * Math.PI * SCORE_RING_RADIUS;

/* const QUIZ_SCOPES = [
  { id: "book", labelKey: "scopeBook" },
  { id: "chapter", labelKey: "scopeChapter" },
  { id: "page", labelKey: "scopePage" },
]; */

/** Session cache so remount / back-to-dashboard does not refetch list every time. */
let quizHistoryCache = { documentId: null, items: null };

const normalizeSavedResult = (item, extras = {}) => {
  if (!item) return null;
  const questions = item.questions || [];
  const correct =
    item.correct_count ?? questions.filter((q) => q.is_correct).length;
  const total = item.total_questions ?? questions.length;
  return {
    ...item,
    ...extras,
    questions,
    total_questions: total,
    correct_count: correct,
    score: item.score ?? (total ? Math.round((correct / total) * 100) : 0),
    feedbackKey: getQuizFeedbackKey(correct, total),
  };
};

const gradeLocally = (questions = [], answers = []) => {
  const answerById = new Map(
    answers.map((item) => [item.question_id, item.user_answer]),
  );
  const graded = questions.map((question) => {
    const userAnswer = answerById.get(question.question_id) ?? null;
    return {
      ...question,
      user_answer: userAnswer,
      is_correct: userAnswer != null && userAnswer === question.correct_answer,
    };
  });
  const correct = graded.filter((q) => q.is_correct).length;
  const total = graded.length;
  return {
    questions: graded,
    total_questions: total,
    correct_count: correct,
    score: total ? Math.round((correct / total) * 100) : 0,
  };
};

const buildOptionState = (question) => {
  if (!question?.options) return {};
  const states = {};
  const correctId = question.correct_answer;
  const userAnswer = question.user_answer;
  question.options.forEach((option) => {
    if (option.option_id === correctId) {
      states[option.option_id] = "correct";
    } else if (option.option_id === userAnswer) {
      states[option.option_id] = "incorrect";
    } else {
      states[option.option_id] = "default";
    }
  });
  return states;
};

const ScoreRing = ({ correct, total, percent, ariaLabel }) => {
  const dashOffset =
    SCORE_RING_CIRCUMFERENCE - (percent / 100) * SCORE_RING_CIRCUMFERENCE;

  return (
    <div className="quiz-score-ring" role="img" aria-label={ariaLabel}>
      <svg viewBox="0 0 80 80" aria-hidden="true">
        <circle
          className="quiz-score-ring-track"
          cx="40"
          cy="40"
          r={SCORE_RING_RADIUS}
        />
        <circle
          className="quiz-score-ring-progress"
          cx="40"
          cy="40"
          r={SCORE_RING_RADIUS}
          strokeDasharray={SCORE_RING_CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
        />
      </svg>
      <span className="quiz-score-fraction">
        {correct}/{total}
      </span>
    </div>
  );
};

/**
 * Status dots + optional expanded Q&A.
 * Session (keepStatusVisible): "Recent Quizzes" header + legend + blue Show Details,
 * then circles; question panel expands when detailsOpen.
 * History: shown only when detailsOpen (entire block).
 */
const QuestionReview = ({
  questions = [],
  detailsOpen = false,
  keepStatusVisible = false,
  onToggleDetails,
  inlineToggle = false,
}) => {
  const { t } = useTranslation();
  const [reviewIndex, setReviewIndex] = useState(0);
  const total = questions.length;
  const reviewQuestion = questions[reviewIndex] || null;
  const showStatus = keepStatusVisible || detailsOpen;

  useEffect(() => {
    setReviewIndex((index) =>
      questions.length ? Math.min(index, questions.length - 1) : 0,
    );
  }, [questions.length]);

  if (!total || !showStatus) return null;

  return (
    <div
      className={`quiz-review-block${inlineToggle ? " quiz-review-block--card" : ""}`}
    >
      <div className="quiz-review-header">
        <div className="quiz-review-header-left">
          {inlineToggle ? (
            <h5 className="quiz-review-title">
              {t("readingAssistant.quiz.recentQuizzes")}
            </h5>
          ) : null}
          <div className="quiz-review-legend-row" aria-hidden="true">
            <span className="quiz-recent-legend-item">
              <span className="quiz-status-dot quiz-status-dot--correct quiz-status-dot--legend">
                <FaCheck />
              </span>
              {t("readingAssistant.quiz.correct")}
            </span>
            <span className="quiz-recent-legend-item">
              <span className="quiz-status-dot quiz-status-dot--incorrect quiz-status-dot--legend">
                <FaTimes />
              </span>
              {t("readingAssistant.quiz.incorrect")}
            </span>
          </div>
        </div>

        {inlineToggle && onToggleDetails ? (
          <button
            type="button"
            className="quiz-details-pill-toggle"
            onClick={onToggleDetails}
            aria-expanded={detailsOpen}
          >
            <span>
              {detailsOpen
                ? t("readingAssistant.quiz.hideDetails")
                : t("readingAssistant.quiz.showDetails")}
            </span>
            {detailsOpen ? (
              <FaChevronUp aria-hidden="true" />
            ) : (
              <FaChevronDown aria-hidden="true" />
            )}
          </button>
        ) : null}
      </div>

      <div className="quiz-status-dots" role="list">
        {questions.map((question, index) => {
          const isCorrect = Boolean(question.is_correct);
          const statusKey = isCorrect ? "correct" : "incorrect";
          return (
            <button
              key={question.question_id || index}
              type="button"
              className={`quiz-status-dot quiz-status-dot--${statusKey}${
                detailsOpen && reviewIndex === index ? " is-active" : ""
              }`}
              onClick={() => {
                setReviewIndex(index);
                if (!detailsOpen && onToggleDetails) onToggleDetails();
              }}
              aria-label={t("readingAssistant.quiz.questionStatusAria", {
                number: index + 1,
                status: t(`readingAssistant.quiz.${statusKey}`),
              })}
              aria-current={
                detailsOpen && reviewIndex === index ? "true" : undefined
              }
            >
              {index + 1}
            </button>
          );
        })}
      </div>

      {detailsOpen && reviewQuestion ? (
        <div className="quiz-review-panel">
          <div className="quiz-review-meta">
            <span>
              {t("readingAssistant.quiz.progress", {
                current: reviewIndex + 1,
                total,
              })}
            </span>
            <span className="quiz-review-type">
              {t("readingAssistant.quiz.multipleChoice")}
            </span>
          </div>
          <p className="quiz-review-question">{reviewQuestion.question_text}</p>
          <div className="ai-quiz-options quiz-review-options" role="list">
            {(reviewQuestion.options || []).map((option, index) => (
              <QuizOption
                key={option.option_id}
                index={index + 1}
                option={option}
                state={
                  buildOptionState(reviewQuestion)[option.option_id] ||
                  "default"
                }
                readOnly
              />
            ))}
          </div>
          {reviewQuestion.explanation ? (
            <div className="ai-quiz-explanation" role="status">
              <strong>{t("readingAssistant.quiz.explanation")}</strong>
              <p>{reviewQuestion.explanation}</p>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/**
 * Shared score card.
 * history → See Details (grey) + Reattempt; details expand below
 * session → Reattempt + Save; dots always visible; Show Details on dots row
 */
const QuizScoreCard = ({
  result,
  mode = "session",
  detailsOpen: detailsOpenProp = false,
  onToggleDetails,
  onReattempt,
  onSave,
  onDelete,
  isGenerating = false,
  actionsLocked = false,
  isSaving = false,
  isDeleting = false,
  errorMessage = "",
  errorKey = "readingAssistant.quiz.error",
}) => {
  const { t } = useTranslation();
  const [sessionDetailsOpen, setSessionDetailsOpen] = useState(false);
  const questions = result?.questions || [];
  const correct = result?.correct_count ?? 0;
  const total = result?.total_questions ?? questions.length;
  const percent =
    result?.score ?? (total ? Math.round((correct / total) * 100) : 0);
  const feedbackKey = result?.feedbackKey || getQuizFeedbackKey(correct, total);
  const isSaved = result?.id != null;
  const isHistory = mode === "history";
  const detailsOpen = isHistory ? detailsOpenProp : sessionDetailsOpen;
  const actionsDisabled = actionsLocked || isGenerating || isDeleting;

  const toggleDetails = () => {
    if (isHistory) onToggleDetails?.();
    else setSessionDetailsOpen((open) => !open);
  };

  return (
    <div className="quiz-score-block">
      <div className="quiz-score-panel">
        <div className="quiz-score-panel-top">
          <p className="quiz-score-heading">
            {t("readingAssistant.quiz.yourScore")}
          </p>
          {isSaved ? (
            <button
              type="button"
              className="quiz-result-delete"
              onClick={() => onDelete?.(result)}
              disabled={isDeleting || actionsLocked}
              aria-label={t("readingAssistant.quiz.deleteResult")}
              title={t("readingAssistant.quiz.deleteResult")}
            >
              <TrashIcon
                className="preserve-original-fill"
                width={14}
                height={18}
                aria-hidden="true"
              />
            </button>
          ) : null}
        </div>

        <div className="quiz-score-body">
          <ScoreRing
            correct={correct}
            total={total}
            percent={percent}
            ariaLabel={t("readingAssistant.quiz.scoreAria", {
              correct,
              total,
            })}
          />
          <div className="quiz-score-copy">
            <p className="quiz-score-feedback">
              {t(`readingAssistant.quiz.feedback.${feedbackKey}`)}
            </p>
            <p className="quiz-score-message">
              {t("readingAssistant.quiz.completedMessage")}
            </p>
          </div>
        </div>
      </div>

      <AiFeedback
        isLoading={false}
        errorMessage={errorMessage}
        errorKey={errorKey}
      />

      <div className="quiz-result-actions">
        {isHistory ? (
          <>
            <button
              type="button"
              className="quiz-result-action quiz-result-action--secondary"
              onClick={toggleDetails}
              disabled={actionsDisabled}
              aria-expanded={detailsOpen}
            >
              <span>
                {detailsOpen
                  ? t("readingAssistant.quiz.hideDetails")
                  : t("readingAssistant.quiz.seeDetails")}
              </span>
            </button>
            <button
              type="button"
              className="quiz-result-action quiz-result-action--primary"
              onClick={onReattempt}
              disabled={actionsDisabled}
              aria-busy={isGenerating}
            >
              <span>{t("readingAssistant.quiz.reattempt")}</span>
              {isGenerating ? (
                <span
                  className="quiz-start-button-spinner"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="quiz-result-action quiz-result-action--secondary"
              onClick={onReattempt}
              disabled={actionsDisabled || isSaving}
              aria-busy={isGenerating}
            >
              <span>{t("readingAssistant.quiz.reattempt")}</span>
              {isGenerating ? (
                <span
                  className="quiz-start-button-spinner"
                  aria-hidden="true"
                />
              ) : null}
            </button>
            <button
              type="button"
              className="quiz-result-action quiz-result-action--primary"
              onClick={onSave}
              disabled={isSaved || isSaving || actionsDisabled}
              aria-busy={isSaving}
            >
              <span>
                {isSaved
                  ? t("readingAssistant.quiz.saved")
                  : isSaving
                    ? t("readingAssistant.quiz.saving")
                    : t("readingAssistant.quiz.save")}
              </span>
            </button>
          </>
        )}
      </div>

      <QuestionReview
        questions={questions}
        detailsOpen={detailsOpen}
        keepStatusVisible={!isHistory}
        onToggleDetails={toggleDetails}
        inlineToggle={!isHistory}
      />
    </div>
  );
};

const QuizTab = () => {
  const { t } = useTranslation();
  const currentPage = useReaderStore((s) => s.currentPage);
  const quizScope = "book";
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(() => {
    const { documentId } = getSessionTokens();
    if (
      documentId &&
      quizHistoryCache.documentId === documentId &&
      Array.isArray(quizHistoryCache.items)
    ) {
      return quizHistoryCache.items;
    }
    return [];
  });
  const [expandedId, setExpandedId] = useState(null);
  const [chapterLabel, setChapterLabel] = useState("");
  /** Which control started generation: "start" | "session" | "history-{id}" | null */
  const [generatingKey, setGeneratingKey] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [resultError, setResultError] = useState("");
  const [resultErrorKey, setResultErrorKey] = useState(
    "readingAssistant.quiz.error",
  );
  const historyFetchRef = useRef(null);
  const generateInflightRef = useRef(null);
  const saveInflightRef = useRef(false);

  const isGenerating = generatingKey != null;

  const clearErrors = useCallback(() => {
    setErrorMessage("");
    setResultError("");
    setResultErrorKey("readingAssistant.quiz.error");
  }, []);

  const buildDisplayTitle = useCallback(
    (scope, context) => {
      if (scope === "book") return t("readingAssistant.quiz.scopeBook");
      if (scope === "page") {
        return t("readingAssistant.quiz.scopePageTitle", {
          page: context?.page_number || currentPage || 1,
        });
      }
      return context?.chapter || t("readingAssistant.quiz.scopeChapter");
    },
    [t, currentPage],
  );

  const setHistoryAndCache = useCallback((itemsOrUpdater) => {
    setHistory((prev) => {
      const next =
        typeof itemsOrUpdater === "function"
          ? itemsOrUpdater(prev)
          : itemsOrUpdater;
      const { documentId } = getSessionTokens();
      quizHistoryCache = { documentId, items: next };
      return next;
    });
  }, []);

  const loadHistory = useCallback(async ({ force = false } = {}) => {
    const { documentId } = getSessionTokens();
    if (!documentId) return;

    if (
      !force &&
      quizHistoryCache.documentId === documentId &&
      Array.isArray(quizHistoryCache.items)
    ) {
      setHistory(quizHistoryCache.items);
      return;
    }

    if (historyFetchRef.current) {
      await historyFetchRef.current;
      return;
    }

    const request = (async () => {
      try {
        const data = await listQuizResults({
          document_id: documentId,
          page: 1,
          page_size: 20,
        });
        const items = Array.isArray(data?.items)
          ? data.items.map((item) => normalizeSavedResult(item)).filter(Boolean)
          : [];
        quizHistoryCache = { documentId, items };
        setHistory(items);
      } catch {
        /* keep current list */
      } finally {
        historyFetchRef.current = null;
      }
    })();

    historyFetchRef.current = request;
    await request;
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const startQuiz = async ({ source = "start" } = {}) => {
    if (generateInflightRef.current || generatingKey != null) return;

    const effectiveScope = "book";
    setGeneratingKey(source);
    clearErrors();

    const request = (async () => {
      try {
        // buildAiContext() uses the reader's current chapter + page for context.
        const context = buildAiContext();
        const displayTitle = buildDisplayTitle(effectiveScope, context);
        setChapterLabel(displayTitle);

        const data = await executeAiAction({
          action_type: "quiz",
          prompt: "Generate 5 single choice questions",
          content: "",
          action_params: {
            question_count: 5,
            quiz_scope: effectiveScope,
          },
        });
        if (!data?.questions?.length) {
          setErrorMessage(
            t("readingAssistant.quiz.error", {
              message: t("readingAssistant.quiz.noQuestions"),
            }),
          );
          return;
        }
        setQuiz({ ...data, displayTitle, quizScope: effectiveScope });
        setResult(null);
        setIsQuizOpen(true);
      } catch (error) {
        const message = t("readingAssistant.quiz.error", {
          message: error?.message || "",
        });
        if (source === "start") setErrorMessage(message);
        else {
          setResultError(error?.message || "");
          setResultErrorKey("readingAssistant.quiz.error");
        }
      } finally {
        generateInflightRef.current = null;
        setGeneratingKey(null);
      }
    })();

    generateInflightRef.current = request;
    await request;
  };

  /**
   * Persist a graded session result. Used for auto-save on finish and manual retry.
   * Skips when already saved or another save is in flight.
   */
  const saveSessionResult = useCallback(
    async (sessionResult) => {
      if (
        !sessionResult ||
        sessionResult.id != null ||
        saveInflightRef.current
      ) {
        return;
      }

      saveInflightRef.current = true;
      setIsSaving(true);
      setResultError("");

      try {
        const { documentId } = getSessionTokens();
        const saved = await saveQuizResult({
          document_id: documentId,
          conversation_id: sessionResult.conversation_id,
          message_id: sessionResult.message_id,
          questions: sessionResult.sourceQuestions || [],
          answers: sessionResult.answers || [],
        });
        const normalized = normalizeSavedResult(saved, {
          displayTitle: sessionResult.displayTitle || chapterLabel,
          quizScope: sessionResult.quizScope || quizScope,
          conversation_id:
            saved?.conversation_id || sessionResult.conversation_id || null,
          message_id: saved?.message_id || sessionResult.message_id || null,
        });

        setResult((prev) => {
          if (!prev || prev.id != null) return prev;
          // Ignore stale save if the user already moved on to another attempt.
          if (
            sessionResult.message_id != null &&
            prev.message_id != null &&
            prev.message_id !== sessionResult.message_id
          ) {
            return prev;
          }
          return {
            ...prev,
            ...normalized,
            sourceQuestions: prev.sourceQuestions,
            answers: prev.answers,
            displayTitle: prev.displayTitle || normalized.displayTitle,
            quizScope: prev.quizScope || normalized.quizScope,
          };
        });
        setHistoryAndCache((prev) => [
          normalized,
          ...prev.filter((item) => item.id !== normalized.id),
        ]);
      } catch (error) {
        setResultError(error?.message || "");
        setResultErrorKey("readingAssistant.quiz.saveError");
      } finally {
        saveInflightRef.current = false;
        setIsSaving(false);
      }
    },
    [chapterLabel, quizScope, setHistoryAndCache],
  );

  const handleComplete = (scorePayload) => {
    const answers = scorePayload?.answers || [];
    const questions = quiz?.questions || [];
    const graded = gradeLocally(questions, answers);
    const sessionResult = {
      ...graded,
      feedbackKey:
        scorePayload?.feedbackKey ||
        getQuizFeedbackKey(graded.correct_count, graded.total_questions),
      conversation_id: quiz?.conversation_id || null,
      message_id: quiz?.message_id || null,
      sourceQuestions: questions,
      answers,
      displayTitle: quiz?.displayTitle || chapterLabel,
      quizScope: quiz?.quizScope || quizScope,
      id: null,
    };
    setResult(sessionResult);
    setIsQuizOpen(false);
    setResultError("");
    void saveSessionResult(sessionResult);
  };

  const handleSave = () => {
    void saveSessionResult(result);
  };

  const removeFromHistory = (resultId) => {
    setHistoryAndCache((prev) => prev.filter((item) => item.id !== resultId));
    setExpandedId((id) => (id === resultId ? null : id));
  };

  const handleDeleteSession = async () => {
    if (!result?.id || deletingId != null) return;
    const resultId = result.id;
    setDeletingId(resultId);
    setResultError("");
    try {
      await deleteQuizResult(resultId);
      removeFromHistory(resultId);
      setResult(null);
      setQuiz(null);
      showInfoNotification("readingAssistant.quiz.deletedToast");
    } catch (error) {
      setResultError(error?.message || "");
      setResultErrorKey("readingAssistant.quiz.deleteError");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteFromHistory = async (item) => {
    if (!item?.id || deletingId != null) return;
    setDeletingId(item.id);
    try {
      await deleteQuizResult(item.id);
      removeFromHistory(item.id);
      showInfoNotification("readingAssistant.quiz.deletedToast");
    } catch (error) {
      setErrorMessage(
        t("readingAssistant.quiz.deleteError", {
          message: error?.message || "",
        }),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleBack = () => {
    setResult(null);
    setQuiz(null);
    clearErrors();
    // History already updated locally on save/delete — no list refetch.
  };

  return (
    <div className="reading-assistant-tab-panel quiz-tab">
      {result ? (
        <div className="quiz-session-view">
          <nav
            className="quiz-result-breadcrumb"
            aria-label={t("readingAssistant.quiz.navigationAria")}
          >
            <button
              type="button"
              className="quiz-result-breadcrumb-link"
              onClick={handleBack}
            >
              {t("readingAssistant.quiz.backToDashboard")}
            </button>
            {chapterLabel || result.displayTitle ? (
              <>
                <span className="quiz-result-breadcrumb-sep" aria-hidden="true">
                  /
                </span>
                <span
                  className="quiz-result-breadcrumb-current"
                  title={chapterLabel || result.displayTitle}
                >
                  {chapterLabel || result.displayTitle}
                </span>
              </>
            ) : null}
          </nav>

          <QuizScoreCard
            result={result}
            mode="session"
            onReattempt={() => startQuiz({ source: "session" })}
            onSave={handleSave}
            onDelete={handleDeleteSession}
            isGenerating={generatingKey === "session"}
            actionsLocked={isGenerating && generatingKey !== "session"}
            isSaving={isSaving}
            isDeleting={result?.id != null && deletingId === result.id}
            errorMessage={resultError}
            errorKey={resultErrorKey}
          />
        </div>
      ) : (
        <div className="quiz-dashboard">
          <div className="quiz-start-card">
            <h4 className="quiz-start-title">
              {t("readingAssistant.quiz.title")}
            </h4>
            <p className="quiz-start-subtitle">
              {t("readingAssistant.quiz.subtitle")}
            </p>

            {/*
            <div className="quiz-scope-block">
              <p className="quiz-scope-label">
                {t("readingAssistant.quiz.scopeLabel")}
              </p>
              <div
                className="quiz-scope-pills"
                role="group"
                aria-label={t("readingAssistant.quiz.scopeLabel")}
              >
                {QUIZ_SCOPES.map((scope) => {
                  const isSelected = quizScope === scope.id;
                  return (
                    <button
                      key={scope.id}
                      type="button"
                      className={`quiz-scope-pill${isSelected ? " is-selected" : ""}`}
                      aria-pressed={isSelected}
                      onClick={() => setQuizScope(scope.id)}
                      disabled={isGenerating}
                    >
                      {t(`readingAssistant.quiz.${scope.labelKey}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            */}

            <AiFeedback
              isLoading={generatingKey === "start"}
              loadingKey="readingAssistant.quiz.loading"
            />
            {generatingKey !== "start" && errorMessage ? (
              <p className="reading-assistant-inline-error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            <button
              type="button"
              className="quiz-start-button"
              onClick={() => startQuiz({ source: "start" })}
              disabled={isGenerating}
              aria-busy={generatingKey === "start"}
            >
              <span>{t("readingAssistant.quiz.cta")}</span>
              {generatingKey === "start" ? (
                <span
                  className="quiz-start-button-spinner"
                  aria-hidden="true"
                />
              ) : (
                <FaChevronRight aria-hidden="true" />
              )}
            </button>
          </div>

          {history.length > 0 ? (
            <section className="quiz-completed-section">
              <h4 className="quiz-completed-section-title">
                {t("readingAssistant.quiz.completedTitle")}
              </h4>
              <p className="quiz-completed-section-disclaimer">
                {t("readingAssistant.quiz.disclaimer")}
              </p>
              <div className="quiz-completed-list">
                {history.map((item) => (
                  <QuizScoreCard
                    key={item.id}
                    result={item}
                    mode="history"
                    detailsOpen={expandedId === item.id}
                    onToggleDetails={() =>
                      setExpandedId((id) => (id === item.id ? null : item.id))
                    }
                    onReattempt={() =>
                      startQuiz({ source: `history-${item.id}` })
                    }
                    onDelete={handleDeleteFromHistory}
                    isGenerating={generatingKey === `history-${item.id}`}
                    actionsLocked={
                      isGenerating && generatingKey !== `history-${item.id}`
                    }
                    isDeleting={deletingId === item.id}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      <QuizModal
        isOpen={isQuizOpen && Boolean(quiz?.questions?.length)}
        quiz={quiz}
        onClose={() => setIsQuizOpen(false)}
        onComplete={handleComplete}
      />
    </div>
  );
};

export default QuizTab;
